import { ColumnState, IRowNode } from '@ag-grid-community/core';
import { MenuItemType, openToast, ToastType } from '@components';
import { AddressDetails } from '@components/Autocomplete/AddressAutoComplete';
import { userRoles } from '@constants/common';
import { datadogRum } from '@datadog/browser-rum';
import { useParams } from '@solidjs/router';
import {
  CustomerDetailState,
  ICustomerDetails,
} from '@store/customers/customerDetails';
import { CreateCustomerState } from '@store/customers/types';
import {
  loadBoardStore,
  oldLoadsStore,
  OldLoadStore,
  setOldLoadsStore,
} from '@store/loadboard';
import { fetchOldLoads } from '@store/loadboard/oldLoads/services';
import { orderStore } from '@store/orders';
import { UserRole, userStore } from '@store/user';
import { LocationLikeT } from '@typeDefinitions/loadTypes';
import { LocationSelect } from '@views/carrierMatching/types';
import { get, intersection, isEmpty, map, mapValues } from 'lodash';
import differenceWith from 'lodash/differenceWith';
import isEqual from 'lodash/isEqual';
import { produce } from 'solid-js/store';
import { ToastPosition } from 'solid-toast';
import { MenuGroupType } from '@components/Select/SelectInputSearchable';

import { oldLoadTotalCountEvent } from './routeOrderDetails';

export * from './printUtils';

export function validURL(str: string): boolean {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i',
  ); // fragment locator
  return pattern.test(str);
}

export function capitalizeWord(str: string): string {
  if (str.length === 0) {
    return str;
  }
  const words = str.split(/(?=[A-Z])/);
  const capitalizedWords = words.map((word) => {
    return word.charAt(0).toUpperCase() + word.slice(1);
  });
  return capitalizedWords.join('');
}

interface NumberFormatOptions {
  localeMatcher?: 'best fit' | 'lookup';
  style?: 'decimal' | 'currency' | 'percent' | 'unit';
  currency?: string;
  currencyDisplay?: 'symbol' | 'narrowSymbol' | 'code' | 'name';
  currencySign?: 'standard' | 'accounting';
  useGrouping?: boolean;
  minimumIntegerDigits?: number;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  minimumSignificantDigits?: number;
  maximumSignificantDigits?: number;
}

export const formatAmount = (
  amount?: number | null,
  options?: NumberFormatOptions,
  customFallback = '-',
  showInCurrencyFormat?: boolean,
) => {
  if (typeof amount === 'number' && !isNaN(amount)) {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
      ...options,
      currency: !isEmpty(get(options, 'currency'))
        ? get(options, 'currency')
        : 'USD',
    });
    let formattedAmount = formatter.format(amount);
    if (Boolean(showInCurrencyFormat) && amount < 0) {
      formattedAmount = `(${formattedAmount.replace('-', '')})`;
    }
    return formattedAmount;
  }

  return customFallback;
};

export const formatNumberWithCommas = (value?: number | null): string => {
  if (typeof value === 'number' && !isNaN(value)) {
    return value.toLocaleString('en-US');
  }
  return value?.toString() ?? '';
};

export function addressFormatter(
  address1: string | undefined | null,
  city: string | undefined | null,
  stateOrProvince: string | undefined | null,
  zip: string | undefined | null,
) {
  let address: string = '';
  if (address1 == undefined) {
    return '';
  }
  address = address1 + ' ' + city + ', ' + stateOrProvince + ' ' + zip;

  return address;
}

export function validEmail(email: string): boolean {
  const emailRegex =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
  return emailRegex.test(email);
}

export const handleToast = (
  type: ToastType,
  errorMessage: string,
  title?: string,
  position: ToastPosition = 'top-right',
  duration = 10_000,
  href?: string,
  linkText?: string,
) => {
  if (type === ToastType.Error) {
    errorMessage = `${errorMessage}\n${
      datadogRum.getInternalContext()?.session_id
    }`;
  }

  openToast({
    type,
    message: errorMessage,
    title: title,
    position,
    duration,
    href,
    linkText,
  });
};

export const handlePopupToast = (type: ToastType, message: string) => {
  if (type === ToastType.Error) {
    message = `${message}\n${datadogRum.getInternalContext()?.session_id}`;
  }

  openToast({
    type,
    message,
    recordActivity: false,
  });
};

export const createSelectMenuItemsFroObjectOfArrays = (
  data: Record<string, string[]>,
) => {
  return mapValues(data, (array) =>
    map(array, (item) => ({ label: item, value: item })),
  );
};

export const convertDateTimeV2 = (date: string, time: string) => {
  if (!date) return time;
  if (!time) return date;

  const [datePart] = date.split('T');
  const [, isoTimePart] = time.split('T');
  if (Boolean(isoTimePart)) {
    return `${datePart}T${isoTimePart}`;
  }

  const timeMatch = time.match(/^(\d{1,2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?)/);
  if (timeMatch?.[1]) {
    return `${datePart}T${timeMatch[1]}`;
  }

  return `${datePart}T00:00:00`;
};

const focusableTags =
  'a:not([disabled]), button:not([disabled]), input:not([disabled]), [tabindex]:not([disabled]):not([tabindex="0"])';

export const focusNextElement = (el = document.activeElement) => {
  if (el === null) {
    return;
  }

  const elements =
    el.parentNode?.querySelectorAll(focusableTags) ?? ([] as Element[]);

  if (elements.length < 2) {
    focusNextElement(el.parentElement);
    return;
  }

  const elIdx = Array.prototype.findIndex.call(elements, (element) => {
    return el === element || element === document.activeElement;
  });

  if (elIdx === elements.length - 1) {
    focusNextElement(el.parentElement);
    return;
  }

  if (elIdx > -1) {
    // @ts-expect-error `focus` is a valid function.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unnecessary-condition
    elements[elIdx + 1]?.focus?.();
  }
};

export const userHasRole = (role: string) => {
  return userStore.user.roles != null && userStore.user.roles.includes(role);
};

const isCreate = () => {
  const params = useParams();
  const isCreate = !params.id;
  return isCreate;
};

// ts-unused-exports:disable-next-line
export const isFullAdmin = () => userHasRole(userRoles.ADMIN);

export const isManager = () => userHasRole(userRoles.MANAGER);

export const isCarrierRelation = () => userHasRole(userRoles.CARRIER_RELATIONS);

export const isSuperAdmin = () => userHasRole(userRoles.SUPER_ADMIN);

export const isAdmin = () => isFullAdmin() || userHasRole(userRoles.ADMIN_LITE);

// ts-unused-exports:disable-next-line
export const isLeadership = () => {
  return userHasRole('Manager') || userHasRole('Supervisor');
};

// ts-unused-exports:disable-next-line
export const isSupervisor = () => {
  return userHasRole('Supervisor');
};

// ts-unused-exports:disable-next-line
export const isAgent = () => {
  return !isLeadership() && !isAdmin();
};

export const isReadonly = () => {
  return (
    intersection(userStore.user.roles, [userRoles.ADMIN, userRoles.ADMIN_LITE])
      .length <= 0
  );
};

export const isLTLAdmin = () => {
  return (
    intersection(userStore.user.roles, [
      userRoles.ADMIN,
      userRoles.ADMIN_LITE,
      userRoles.SUPER_ADMIN,
    ]).length > 0
  );
};

export const customerEditable = () => {
  if (isSuperAdmin()) return true;
  else if (isAdmin() && orderStore.order.status === 'Open') return true;
  return isCreate();
};

export const canReassign = () => {
  return (
    intersection(userStore.user.roles, [
      userRoles.ADMIN,
      userRoles.MANAGER,
      userRoles.SUPERVISOR,
    ]).length > 0
  );
};

export const isOfficeManager = () => {
  return (
    userStore.user.roles &&
    userStore.user.roles.includes('Manager') &&
    !userStore.user.roles.includes('Admin') &&
    !userStore.user.roles.includes('AdminLite') &&
    !userStore.user.roles.includes('Recruiter')
  );
};

export const isCapacity = () => {
  return userHasRole(userRoles.CAPACITY) as boolean;
};

export const isPrivate = () => {
  return (
    isCapacity() ||
    orderStore.order.loads.some(
      (load) => load.sharedWithOfficeId === userStore.user.officeId,
    )
  );
};
export const orderTableErrors = (
  fields: string[],
  path: string,
  index: number,
) => {
  const errors: string[] = [];
  fields.forEach((field) => {
    if (!orderStore.orderFormError) return null;
    const fieldError =
      orderStore.orderFormError[
        path.replace('{index}', `${index}`).replace('{field}', field)
      ];
    if (Array.isArray(fieldError)) {
      errors.push(fieldError[0]);
    }
  });
  return errors;
};

export const getEligibleEntries = <
  T extends { id: number; operationType: string },
>(
  items: T[] | undefined | null,
): T[] => {
  return items?.filter((item) => item.operationType !== 'Delete') || [];
};

export const sdbm = (str: string) => {
  const letters = str.split('');
  return letters.reduce((code, letter) => {
    return (code = letter.charCodeAt(0) + (code << 6) + (code << 16) - code);
  }, 0);
};

export const findMenuItemByValue = (
  value: string,
  items: MenuItemType[],
): MenuItemType => {
  return (
    items.find((item) => item.value === value) ?? {
      value: '',
      label: '',
    }
  );
};

export const doesExternalFilterFactory = (
  filter: string,
  handlerType: 'group' | 'customer',
) => {
  if (handlerType === 'group') {
    return function (node: IRowNode) {
      const data = (node as { data: unknown }).data;
      const members = loadBoardStore.membersByGroupName[filter];
      if (members === undefined) {
        return false;
      }

      return (
        members.includes((data as { assignedToId: string }).assignedToId) ||
        members.includes((data as { ownerId: string }).ownerId)
      );
    };
  }

  return function (node: unknown) {
    const data = (node as { data: unknown }).data;
    const int: number = Object.values(
      // This guards against a very specific scenario where there is no office group match.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      loadBoardStore.officeGroupIdsByMachingNames[filter] ?? {},
    )[0];
    const string: string = int.toString();

    if ((data as { customerGroups: string }).customerGroups) {
      const comp = (data as { customerGroups: string }).customerGroups
        .split(',')
        .includes(string);
      return comp;
    }
    return false;
  };
};

export const isExternalFilterPresent = () => true;

export const getSortModelFromColumnState = (
  columns: ColumnState[] | undefined,
) => {
  if (!columns) return [];
  return columns
    .filter((c) => c.sort !== null)
    .map((c) => ({
      colId: c.colId,
      sort: c.sort,
      sortIndex: c.sortIndex ?? 0,
    }))
    .sort((a, b) => (a.sortIndex >= b.sortIndex ? 1 : -1));
};

export function invalidUrlPatterns(string: string) {
  const invalidPatterns = ['//.'];
  return invalidPatterns.some((pattern) => string.indexOf(pattern) !== -1);
}

export function isValidHttpUrl(string: string) {
  const pattern = new RegExp(
    '^(http(s)?:\\/\\/)' + // protocol
      '((\\bw{3}\\.\\b))?' +
      '[-a-zA-Z0-9@@:%._+~#=]{1,256}\\.' +
      '[a-zA-Z0-9()]{1,6}' +
      '\\b(?:[-a-zA-Z0-9()@@:%_\\+.~#?&\\/=]*)$',
  );
  return pattern.test(string);
}

export const hasPermission = (permission: string) => {
  return (
    userStore.user.permissions &&
    userStore.user.permissions.length > 0 &&
    userStore.user.permissions.includes(permission)
  );
};

export const handleGooglePlacesSelect = (
  place: AddressDetails,
  updateFormState: (state: Partial<ICustomerDetails>) => void,
  prefix: string,
) => {
  const {
    address1 = '',
    city = '',
    state = '',
    zipCode = '',
    country = '',
  } = place;

  const countryCodes = {
    'United States': 'USA',
    Canada: 'CAN',
    Mexico: 'MEX',
  };
  if (Boolean(updateFormState)) {
    updateFormState({
      [`${prefix}Address1`]: address1,
      [`${prefix}City`]: city,
      [`${prefix}State`]: state,
      [`${prefix}Zip`]: zipCode,
      [`${prefix}CountryCode`]:
        countryCodes[country as 'United States' | 'Canada' | 'Mexico'],
    });
  }
};

export const setSameAsOtherAddress = (
  sourceType: 'mailing' | 'physical',
  customerStore: CreateCustomerState | CustomerDetailState,
  setupNewCustomerState: (state: Partial<ICustomerDetails>) => void,
) => {
  const targetType = sourceType === 'mailing' ? 'physical' : 'mailing';
  const { customer }: { customer: Partial<ICustomerDetails> } = customerStore;

  setupNewCustomerState({
    [`${targetType}Address1`]: customer[`${sourceType}Address1`],
    [`${targetType}Address2`]: customer[`${sourceType}Address2`],
    [`${targetType}City`]: customer[`${sourceType}City`],
    [`${targetType}State`]: customer[`${sourceType}State`],
    [`${targetType}Zip`]: customer[`${sourceType}Zip`],
    [`${targetType}CountryCode`]: customer[`${sourceType}CountryCode`],
  });
};

export const getActiveStopIndices = () => {
  const indices: number[] = [];
  orderStore.order.loads[orderStore.activeTab.index]?.stops?.forEach(
    (stop, index) => {
      if (stop.operationType !== 'Delete') {
        indices.push(index);
      }
    },
  );
  return indices;
};

export const createMaxNumber = (
  digitsBeforeDecimal: number,
  digitsAfterDecimal: number,
) => {
  const integerPart = '9'.repeat(digitsBeforeDecimal);
  const fractionalPart = '9'.repeat(digitsAfterDecimal);
  const maxNumber = `${integerPart}.${fractionalPart}`;
  return parseFloat(maxNumber);
};

export const trimDecimalValue = (
  value: string,
  maxPreDecimal: number,
  maxPostDecimal: number,
  allowNegative: boolean,
): string => {
  const cleanedValue = value.replace(/(?!^)-/g, '');

  const isNegative = allowNegative && cleanedValue.startsWith('-');
  const absoluteValue = isNegative ? cleanedValue.slice(1) : cleanedValue;

  const [integerPart, fractionalPart] = absoluteValue.split('.');

  const trimmedIntegerPart = integerPart.slice(0, maxPreDecimal);

  const trimmedFractionalPart = fractionalPart
    ? fractionalPart.slice(0, maxPostDecimal)
    : '';

  let trimmedValue = trimmedFractionalPart
    ? `${trimmedIntegerPart}.${trimmedFractionalPart}`
    : trimmedIntegerPart;

  if (absoluteValue.endsWith('.') && !trimmedFractionalPart) {
    trimmedValue += '.';
  }

  return isNegative ? `-${trimmedValue}` : trimmedValue;
};

export const preventHyphen = (event: KeyboardEvent) => {
  if (event.key === '-') {
    event.preventDefault();
  }
};

export const clampMileageValue = (value: unknown): number => {
  const numericValue = value as number;
  return numericValue > createMaxNumber(7, 2)
    ? Number(trimDecimalValue(String(numericValue), 7, 2, false))
    : numericValue;
};

export const getOldLoadCount = async () => {
  if (oldLoadsStore.oldLoadsCount === 0 && userStore.user.id > 0) {
    const res = await fetchOldLoads({
      filters: [
        {
          field: 'days',
          filterType: 'EQUALS',
          values: ['7'],
        },
      ],
      sort: {
        field: 'dropDate',
        direction: 'desc',
      },
      pageNumber: 1,
      pageSize: 500,
    });
    oldLoadTotalCountEvent(res?.totalCount as number);
    setOldLoadsStore(
      produce((draft: OldLoadStore) => {
        draft.oldLoadsCount = res?.totalCount as number;
      }),
    );
  }
};

export const getHomeRoute = () => {
  if (userHasRole(UserRole.Accounting)) {
    return '/accounting/billingqueue';
  } else if (userHasRole(UserRole.Manager)) {
    if (userHasPermission('Hide Dashboard')) {
      return '/loadboard';
    }
    return '/dashboard';
  } else if (userHasRole(UserRole.CarrierRelations)) {
    return '/carrier';
  } else if (userHasRole(UserRole.Collections)) {
    return '/accounting/collections';
  } else if (userHasRole(UserRole.Recruiter)) {
    return '/office';
  } else if (userStore.user.name === 'oncall@armstrongtransport.com') {
    return '/oncall/index';
  }

  return '/Load/Index';
};

export const areArraysEqual = <T>(arrA: T[], arrB: T[]): boolean => {
  const comparator = (objA: T, objB: T) => {
    return isEqual(objA, objB);
  };

  const diffA = differenceWith(arrA, arrB, comparator);
  const diffB = differenceWith(arrB, arrA, comparator);

  return diffA.length === 0 && diffB.length === 0;
};

export const getDefaultLocationValue = (
  data: LocationLikeT | LocationSelect,
  part?: 'origin' | 'destination',
) => {
  if ('display' in data) {
    return data.display;
  }

  if (
    part !== undefined &&
    Boolean(data[`${part}State`] && data[`${part}City`])
  ) {
    return `${data[`${part}City`]}, ${data[`${part}State`]}, ${
      data[`${part}Zip`]
    }`;
  }

  return '';
};

export const isOrderStoreDirty = () => {
  const noChanges =
    // TODO: Check why operation type is 'Update' for loads in orderstore on refresh
    // orderStore.order.loads.every((load) => load.operationType === 'None') &&
    (orderStore.order.vendors?.every(
      (vendor) => vendor.operationType === 'None',
    ) ??
      true) &&
    orderStore.order.lineItems.every(
      (lineItem) => lineItem.operationType === 'None',
    ) &&
    orderStore.order.loads.every((load) =>
      load.lineItems?.every((lineItem) => lineItem.operationType === 'None'),
    ) &&
    orderStore.order.loads.every((load) =>
      load.stops?.every((stop) => stop.operationType === 'None'),
    ) &&
    orderStore.order.loads.every((load) =>
      load.payments?.every((payment) => payment.operationType === 'None'),
    ) &&
    orderStore.order.loads.every((load) =>
      load.items?.every((item) => item.operationType === 'None'),
    );

  return !noChanges;
};

export const userHasPermission = (role: string) => {
  if (userStore.user.permissions) {
    return userStore.user.permissions.includes(role);
  }
  return false;
};

export function groupByDisabledHeaders(items: MenuItemType[]): MenuGroupType[] {
  const groups: MenuGroupType[] = [];
  let current: MenuGroupType | null = null;

  for (const item of items) {
    if (item.disabled) {
      current = { label: item.label as string, items: [] };
      groups.push(current);
    } else if (current) {
      current.items.push({ label: item.label as string, value: item.value });
    }
  }

  return groups;
}
